// Epoch 75: Reusable searchable campaign dropdown
// Replaces native <select> for CSR campaign selection with search + two-line display
// Date: March 2026

import React, { useState, useEffect, useRef, useMemo } from 'react';

export interface CampaignOption {
  id: string;
  name: string;
  company_name?: string;
  target_list_name?: string;
  status?: string;
}

interface CampaignSearchSelectProps {
  campaigns: CampaignOption[];
  value: string;
  onChange: (campaignId: string) => void;
  placeholder?: string;
  recentCampaignIds?: string[];
  disabled?: boolean;
  className?: string;
  allowEmpty?: boolean;
}

const CampaignSearchSelect: React.FC<CampaignSearchSelectProps> = ({
  campaigns,
  value,
  onChange,
  placeholder = '-- Select a campaign --',
  recentCampaignIds = [],
  disabled = false,
  className = '',
  allowEmpty = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedCampaign = campaigns.find(c => c.id === value);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  // Filter campaigns by search term across name, company, and target list
  const filteredCampaigns = useMemo(() => {
    if (!searchTerm.trim()) return campaigns;
    const lower = searchTerm.toLowerCase();
    return campaigns.filter(c =>
      c.name.toLowerCase().includes(lower) ||
      (c.company_name && c.company_name.toLowerCase().includes(lower)) ||
      (c.target_list_name && c.target_list_name.toLowerCase().includes(lower))
    );
  }, [campaigns, searchTerm]);

  // Recently used campaigns (respects search filter)
  const recentCampaigns = useMemo(() => {
    if (recentCampaignIds.length === 0) return [];
    const filteredIds = new Set(filteredCampaigns.map(c => c.id));
    return recentCampaignIds
      .filter(id => filteredIds.has(id))
      .map(id => campaigns.find(c => c.id === id))
      .filter(Boolean) as CampaignOption[];
  }, [recentCampaignIds, filteredCampaigns, campaigns]);

  // All campaigns minus recently used (collapsed when searching)
  const allCampaigns = useMemo(() => {
    if (recentCampaignIds.length === 0 || searchTerm.trim()) return filteredCampaigns;
    const recentIds = new Set(recentCampaignIds);
    return filteredCampaigns.filter(c => !recentIds.has(c.id));
  }, [filteredCampaigns, recentCampaignIds, searchTerm]);

  const handleSelect = (campaignId: string) => {
    onChange(campaignId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const formatLabel = (campaign: CampaignOption) => {
    const parts = [campaign.company_name, campaign.name].filter(Boolean);
    const base = parts.join(' — ');
    return campaign.target_list_name ? `${base} — 📋 ${campaign.target_list_name}` : base;
  };

  const CampaignItem: React.FC<{ campaign: CampaignOption }> = ({ campaign }) => (
    <div
      onClick={() => handleSelect(campaign.id)}
      className={`px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors ${
        value === campaign.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''
      }`}
    >
      <div className="text-sm font-medium text-gray-900">
        {campaign.company_name
          ? `${campaign.company_name} — ${campaign.name}`
          : campaign.name}
      </div>
      {campaign.target_list_name && (
        <div className="text-xs text-gray-400 mt-0.5">
          📋 {campaign.target_list_name}
        </div>
      )}
    </div>
  );

  return (
    <div ref={containerRef} className={`relative ${className}`} onKeyDown={handleKeyDown}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full text-left px-4 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-purple-600 ${
          disabled
            ? 'bg-gray-100 cursor-not-allowed text-gray-400'
            : 'hover:border-gray-400 cursor-pointer'
        } ${isOpen ? 'border-purple-600 ring-2 ring-purple-600' : 'border-gray-300'}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            {selectedCampaign ? (
              <div className="text-sm font-medium text-gray-900 truncate">
                {formatLabel(selectedCampaign)}
              </div>
            ) : (
              <span className="text-gray-400 text-sm">{placeholder}</span>
            )}
          </div>
          <svg
            className={`ml-2 h-4 w-4 text-gray-400 flex-shrink-0 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
          {/* Search input */}
          <div className="p-2 border-b border-gray-100 flex-shrink-0">
            <input
              ref={searchRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search campaign, company, or list name..."
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </div>

          {/* Options list */}
          <div className="overflow-y-auto flex-1">
            {/* Clear / none option */}
            {allowEmpty && (
              <div
                onClick={() => handleSelect('')}
                className="px-4 py-2 text-sm text-gray-400 cursor-pointer hover:bg-gray-50 border-b border-gray-100"
              >
                — None —
              </div>
            )}

            {filteredCampaigns.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                No campaigns match &ldquo;{searchTerm}&rdquo;
              </div>
            ) : (
              <>
                {/* Recently Used section */}
                {recentCampaigns.length > 0 && (
                  <>
                    <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                      📌 Recently Used
                    </div>
                    {recentCampaigns.map(c => (
                      <CampaignItem key={`recent-${c.id}`} campaign={c} />
                    ))}
                    {allCampaigns.length > 0 && (
                      <div className="border-t border-gray-100" />
                    )}
                  </>
                )}

                {/* All campaigns */}
                {allCampaigns.length > 0 && (
                  <>
                    {recentCampaigns.length > 0 && (
                      <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                        All Campaigns
                      </div>
                    )}
                    {allCampaigns.map(c => (
                      <CampaignItem key={c.id} campaign={c} />
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignSearchSelect;
